import { MouseEvent, useMemo } from 'react';
import { InvoiceItem } from '../../../../core/models';
import {
    GridCellParams,
    GridRowSelectionModel,
    GRID_CHECKBOX_SELECTION_FIELD,
    MaterialDataGrid,
    MuiEvent,
    GridPagination,
    GridPaginationModel,
} from '@enterprise/material-data-grid';
import styles from './itemsGrid.module.scss';
import classNames from 'classnames';
import { GridState, ItemEditQuery, PaginationModel, SelectionModel } from '../../../../store/invoiceItem.store';
import { ItemsGridToolbar } from './toollbar';
import { customColumnMenu } from './columnMenu';
import { ItemsGridColumn, ItemsType, useItemGrid } from './hooks';
import { BulkUpdateFlyoverType } from '../ItemsPageStore';
import { isEqual, omit } from 'lodash';

const DEFAULT_PAGE_SIZE = 25;

export interface ItemResultsProps {
    className?: string;
    invoiceItems?: InvoiceItem[];
    grouping?: ItemsGridColumn[];
    selection?: SelectionModel;
    pagination?: PaginationModel;
    onSelectionChange?: (params: SelectionModel) => void;
    onDownloadPreview?: () => void;
    onEdit?: (params: { query: ItemEditQuery }) => void;
    onBulkActionChange?: (params: { type: BulkUpdateFlyoverType }) => void;
    loading?: boolean;
    gridState?: GridState;
    onGridStateChange?: (state: GridState) => void;
    type?: ItemsType;
}

export const ItemsGrid = (props: ItemResultsProps) => {
    const {
        invoiceItems = [],
        onEdit,
        className,
        grouping,
        selection,
        pagination,
        onSelectionChange,
        onDownloadPreview,
        onBulkActionChange,
        loading,
        gridState,
        onGridStateChange,
        type,
    } = props;

    const defaultState: GridState = {
        groupingModel: grouping,
        visibilityModel: grouping?.reduce((agg, item) => {
            return { ...agg, [item]: false };
        }, {} as Record<number | string, boolean | undefined>),
        pageSize: DEFAULT_PAGE_SIZE,
        searchQuery: gridState?.searchQuery,
    };

    const columnsState = gridState || defaultState;
    const { pageSize = DEFAULT_PAGE_SIZE, page = 0 } = columnsState;

    const {
        apiRef, //
        columns,
        groupColumn,
        getRowId,
        getQuery,
        state,
    } = useItemGrid({
        columnsState,
        onColumnsStateChange: onGridStateChange,
        itemsType: type,
    });

    const {
        pinnedColumns,
        onPinnedColumnsChange,
        onColumnWidthChange,
        columnVisibilityModel,
        onColumnVisibilityModelChange,
        sortModel,
        onSortModelChange,
        rowGroupingModel,
        onRowGroupingModelChange,
        onColumnOrderChange,
    } = state;

    const headerHeight = 68;
    const rowHeight = 52;

    const idToInvoiceItems = useMemo(() => {
        return new Map<string, InvoiceItem>(invoiceItems.map((item) => [getRowId(item), item]));
    }, [invoiceItems]);

    const selectionModel = useMemo(() => {
        const selectedIds = selection?.all ? idToInvoiceItems.keys() : selection?.items?.keys();

        return selection?.all && selection?.excluded
            ? ((selectedIds && [...selectedIds]) || []).filter((id) => !selection?.excluded?.has(id))
            : (selectedIds && [...selectedIds]) || [];
    }, [idToInvoiceItems, selection]);

    const onSelectionModelChange = (selectionModel: GridRowSelectionModel) => {
        if (!onSelectionChange) {
            return;
        }

        const items = selectionModel.reduce((agg, id) => {
            const invoiceItem = idToInvoiceItems.get(id as string) || selection?.items?.get(id as string);
            if (invoiceItem) {
                agg.set(id as string, invoiceItem);
            }

            return agg;
        }, new Map<string, InvoiceItem>());
        onSelectionChange({ items });
    };

    const onSelectionAllChange = (params: { value: boolean }) => {
        onSelectionChange?.({ ...selection, all: params.value });
    };

    const onExitBulkMode = () => {
        onSelectionChange?.({ all: false, excluded: undefined, items: undefined });
    };

    const onCellClick = (params: GridCellParams, event: MuiEvent<MouseEvent>) => {
        if (params.field === GRID_CHECKBOX_SELECTION_FIELD) {
            return;
        }

        const query = getQuery(params);

        if (!query) {
            return;
        }

        event.preventDefault();
        onEdit?.({ query });
    };

    const getRowClassName = () => {
        return styles.row;
    };

    const toolbar = () => {
        const onColumnsReset = () => {
            onGridStateChange?.(defaultState);
        };

        return (
            <ItemsGridToolbar
                selection={selection}
                pagination={pagination}
                onSelectionAllChange={onSelectionAllChange}
                onDownloadPreview={onDownloadPreview}
                onExitBulkMode={onExitBulkMode}
                onAddToPractices={() => onBulkActionChange?.({ type: BulkUpdateFlyoverType.AddToPractices })}
                isResetVisible={!isEqual(omit(columnsState, 'page'), defaultState)}
                onColumnsReset={onColumnsReset}
            />
        );
    };

    const onPaginationModelChange = (model: GridPaginationModel) => {
        onGridStateChange?.({ ...columnsState, page: model.page, pageSize: model.pageSize });
    };

    return (
        <MaterialDataGrid
            rowCount={pagination?.groups || 0}
            keepNonExistentRowsSelected
            className={classNames(styles.holder, className)}
            data-automation-id="select-all-data-grid"
            loading={loading}
            rows={invoiceItems}
            columns={columns}
            apiRef={apiRef}
            rowHeight={rowHeight}
            columnHeaderHeight={headerHeight}
            // TODO: turned on on smaller screens - has impact on performance on small screens
            autoHeight={false}
            rowGroupingColumnMode="multiple"
            checkboxSelection
            getRowId={getRowId}
            getRowClassName={getRowClassName}
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={onSelectionModelChange}
            groupingColDef={groupColumn}
            onCellClick={onCellClick}
            disableRowSelectionOnClick={true}
            disableColumnFilter={false}
            disableColumnResize={false}
            pinnedColumns={pinnedColumns}
            onPinnedColumnsChange={onPinnedColumnsChange}
            onColumnWidthChange={onColumnWidthChange}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            rowGroupingModel={rowGroupingModel}
            onRowGroupingModelChange={(model) => onRowGroupingModelChange(model as ItemsGridColumn[])}
            onColumnOrderChange={onColumnOrderChange}
            hideFooterPagination={false}
            hideFooter={false}
            pagination
            paginationModel={{ page, pageSize }}
            pageSizeOptions={[25, 50, 100, 200, 500]}
            paginationMode="server"
            onPaginationModelChange={onPaginationModelChange}
            components={{
                Toolbar: toolbar,
                ColumnMenu: customColumnMenu({ selection, onBulkActionChange }),
                Pagination: (props) => <GridPagination {...props} showFirstButton={true} showLastButton={true} />,
            }}
            classes={{
                groupingCriteriaCell: styles.groupingCriteriaCell,
                groupingCriteriaCellToggle: styles.groupingCriteriaCellToggle,
                columnHeaders: styles.columnHeaders,
                columnHeader: styles.columnHeader,
                pinnedColumnHeaders: styles.pinnedColumnHeaders,
                pinnedColumns: styles.pinnedColumns,
                footerContainer: styles.footerContainer,
                selectedRowCount: styles.selectedRowCount,
            }}
        />
    );
};
